import { SelectionItemId } from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IFilterItem } from '../data-types/filterItem';
import { getFilteredFilterItems } from '../utils/FilterUtils';

export interface IUseFilterPickerStateOptions {
    items: IFilterItem[];
    committedSelection: IFilterItem[];
    filterName: string;
    isFiltered: boolean;
    noResultsText: string;
    searchItemName: string;
    searchValue?: string;
    setSearchValue?: (searchValue: string) => void;
    onCommit: (filteredItems: IFilterItem[], filterName: string) => void;
    overrideAnnouncedMessage?: string;
}

export interface IUseFilterPickerStateResult {
    draftSelection: IFilterItem[];
    draftSelectedIds: SelectionItemId[];
    visibleItems: IFilterItem[];
    hasResults: boolean;
    announcementMessage: string;
    showClearAll: boolean;
    onSearchChange: (newValue: string | undefined) => void;
    onAbortSearch: () => void;
    onSelectionChange: (selectedIds: SelectionItemId[]) => void;
    clearAll: () => void;
    apply: (closePicker?: () => void) => void;
    cancel: (closePicker?: () => void) => void;
    resetDraft: () => void;
}

export const useFilterPickerState = (options: IUseFilterPickerStateOptions): IUseFilterPickerStateResult => {
    const {
        items,
        committedSelection,
        filterName,
        isFiltered,
        noResultsText,
        searchItemName,
        searchValue,
        setSearchValue,
        onCommit,
        overrideAnnouncedMessage,
    } = options;

    const [draftSelection, setDraftSelection] = useState<IFilterItem[]>(committedSelection ?? []);
    // Holds selection just applied so dismiss doesn't revert to stale committed props
    // before parent state/URL updates (e.g. useSelectedEntities search params).
    const pendingAppliedSelectionRef = useRef<IFilterItem[] | null>(null);
    const committedSelectionKey = useMemo(
        () =>
            (committedSelection ?? [])
                .map((item) => item.key)
                .sort()
                .join('\0'),
        [committedSelection]
    );

    useEffect(() => {
        setDraftSelection(committedSelection ?? []);
        pendingAppliedSelectionRef.current = null;
        // Sync when committed keys change, not when callers pass a new array with the same selection
        // (e.g. getFilteredEntities() in ReportLinesTable on every parent re-render).
    }, [committedSelectionKey]);

    const visibleItems = useMemo(() => getFilteredFilterItems(items, searchValue), [items, searchValue]);
    const hasResults = visibleItems.length > 0;

    const draftSelectedIds = useMemo(() => draftSelection.map((item) => item.key), [draftSelection]);

    const announcementMessage = useMemo(() => {
        if (overrideAnnouncedMessage !== undefined) {
            return overrideAnnouncedMessage;
        }

        const filteredCount = visibleItems.length;
        const unfilteredCount = items.length;
        if (filteredCount === unfilteredCount) {
            return '';
        }

        if (!hasResults) {
            return noResultsText;
        }

        return `${filteredCount} ${searchItemName}${filteredCount === 1 ? '' : 's'} found`;
    }, [overrideAnnouncedMessage, visibleItems.length, items.length, hasResults, noResultsText, searchItemName]);

    const showClearAll = isFiltered || draftSelection.length > 0;

    const onSearchChange = useCallback(
        (newValue: string | undefined) => {
            setSearchValue?.(newValue ?? '');
        },
        [setSearchValue]
    );

    const onAbortSearch = useCallback(() => {
        setSearchValue?.('');
    }, [setSearchValue]);

    const onSelectionChange = useCallback(
        (selectedIds: SelectionItemId[]) => {
            const selectedIdSet = new Set(selectedIds.map(String));
            const stillSelected = draftSelection.filter((item) => selectedIdSet.has(item.key));
            const stillSelectedKeys = new Set(stillSelected.map((item) => item.key));
            const newlySelected = items.filter((item) => selectedIdSet.has(item.key) && !stillSelectedKeys.has(item.key));
            setDraftSelection([...stillSelected, ...newlySelected]);
        },
        [draftSelection, items]
    );

    const resetDraft = useCallback(() => {
        const nextSelection = pendingAppliedSelectionRef.current ?? committedSelection ?? [];
        pendingAppliedSelectionRef.current = null;
        setDraftSelection(nextSelection);
        setSearchValue?.('');
    }, [committedSelection, setSearchValue]);

    const apply = useCallback(
        (closePicker?: () => void) => {
            pendingAppliedSelectionRef.current = draftSelection;
            onCommit(draftSelection, filterName);
            closePicker?.();
        },
        [draftSelection, filterName, onCommit]
    );

    const cancel = useCallback(
        (closePicker?: () => void) => {
            pendingAppliedSelectionRef.current = null;
            if (closePicker) {
                closePicker();
            } else {
                resetDraft();
            }
        },
        [resetDraft]
    );

    const clearAll = useCallback(() => {
        pendingAppliedSelectionRef.current = [];
        onCommit([], filterName);
    }, [filterName, onCommit]);

    return {
        draftSelection,
        draftSelectedIds,
        visibleItems,
        hasResults,
        announcementMessage,
        showClearAll,
        onSearchChange,
        onAbortSearch,
        onSelectionChange,
        clearAll,
        apply,
        cancel,
        resetDraft,
    };
};
